import { useMutation, useQuery, useQueryClient } from 'react-query'
import APIService from 'api/APIService'
import { ID, JobStatus, PayPeriodActions } from 'types/entity'
import { useSettings } from 'core/hooks/useSettings'
import useNotifications from 'core/hooks/useNotifications'

interface PayPeriodActionProps {
  onSuccess?: VoidFunction
}

interface PayPeriodLocal {
  id: ID
  action: PayPeriodActions | ''
  jobId: ID
}

const localStorageKey = 'pay_period_action_job'

// TODO: this is ugly but fast to test. If it works create a util function
const getSessionStorageData = () => {
  const sessionStorageData = sessionStorage.getItem(localStorageKey)
  let payPeriodData: PayPeriodLocal = {
    id: '',
    action: '',
    jobId: ''
  }
  try {
    if (sessionStorageData) {
      payPeriodData = JSON.parse(sessionStorageData)
    }
  } catch (e) {
    console.error(e)
  }
  return payPeriodData
}
const updateSessionStorage = (payPeriodUpdateData: Partial<PayPeriodLocal>) => {
  const payPeriodData = getSessionStorageData()
  sessionStorage.setItem(
    localStorageKey,
    JSON.stringify({
      ...payPeriodData,
      ...payPeriodUpdateData
    })
  )
}

const usePayPeriodAction = ({ onSuccess }: PayPeriodActionProps = {}) => {
  const queryClient = useQueryClient()
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const payPeriodData = getSessionStorageData()
  const jobID = settings.pollingJobs.payPeriodActionJobId || payPeriodData.jobId

  useQuery(
    ['pay-period-action-polling', jobID],
    () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    {
      enabled: !!jobID,
      refetchInterval: 2000,
      onSuccess: (response: JobStatus) => {
        const payPeriodData = getSessionStorageData()
        if (response.status === 'finished' && !response.data?.errors) {
          notification.dismiss('payPeriodActionJobId')
          updateSessionStorage({
            ...payPeriodData,
            jobId: ''
          })
          set('pollingJobs', {
            ...settings.pollingJobs,
            payPeriodActionJobId: ''
          })
          queryClient.invalidateQueries(
            payPeriodData.id
              ? ['pay-periods', payPeriodData.id]
              : ['pay-periods']
          )
          queryClient.invalidateQueries(['reports'])
          notification.success({
            title: response.data.message as string,
            id: String(jobID)
          })
        }

        if (
          response.status === 'failed' ||
          (response.status === 'finished' && response.data?.errors)
        ) {
          notification.dismiss('payPeriodActionJobId')
          notification.error({
            title: 'There were some errors updating your pay period',
            subtitle: (
              <div>
                {typeof response.data?.message === 'string' &&
                  response.data.message?.split(';').map((error) => {
                    return <span>{error.replace(';', '')}</span>
                  })}
              </div>
            ),
            id: String(jobID)
          })
          updateSessionStorage({
            ...payPeriodData,
            jobId: ''
          })
          set('pollingJobs', {
            ...settings.pollingJobs,
            payPeriodActionJobId: ''
          })
        }

        if (response.status === 'finished') {
          console.log('invalidateQueries', payPeriodData)
          queryClient.invalidateQueries(
            payPeriodData.id
              ? ['pay-periods', payPeriodData.id]
              : ['pay-periods']
          )
          queryClient.invalidateQueries(['reports'])
          updateSessionStorage({
            id: '',
            action: '',
            jobId: ''
          })
          // setTimeout(() => {
          // }, 1000)
        }
      },
      onError: () => {
        const payPeriodData = getSessionStorageData()
        notification.dismiss('payPeriodActionJobId')
        notification.error({
          title: 'Could not update pay period at this time',
          id: String(jobID)
        })
        updateSessionStorage({
          ...payPeriodData,
          jobId: ''
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          payPeriodActionJobId: ''
        })
      }
    }
  )

  // Query to update pay period - returns jobID
  let selectedAction: PayPeriodActions
  const updateQuery = useMutation(
    ({ id, action }: { id: ID; action: PayPeriodActions }) => {
      selectedAction = action
      updateSessionStorage({
        ...payPeriodData,
        action,
        id
      })
      return APIService.payPeriods.BatchUpdatePayPeriodReports({
        id,
        action
      })
    },
    {
      onSuccess: (data) => {
        const payPeriodData = getSessionStorageData()
        notification.loading({
          title:
            selectedAction === 'approve'
              ? 'Approving in progress...'
              : 'Updating pay period...',
          subtitle:
            'This may take a few minutes. You can close this pop up and we’ll keep working in the background.',
          duration: Infinity,
          id: 'payPeriodActionJobId'
        })
        setTimeout(() => {
          set('pollingJobs', {
            ...settings.pollingJobs,
            payPeriodActionJobId: data.token_id
          })
        }, 0)
        updateSessionStorage({
          ...payPeriodData,
          jobId: data.token_id
        })
        onSuccess?.()
      },
      onError: () => {
        notification.error({
          title: 'An error has occurred trying to update your pay period',
          id: 'payPeriodAction'
        })
      }
    }
  )

  return {
    updateQuery
  }
}

export default usePayPeriodAction
